/**
 * Stable anonymous client-side UUID written to localStorage on first visit.
 *
 * Powers the nightly co-download matrix (lib/recommendations.ts) by giving
 * the funnel_events stream a per-browser identity that survives across
 * sessions. Not PII — no name/email/profile attached, never sent except as
 * a property on the user's own events.
 *
 * SSR-safe: returns '' on the server. Callers must avoid baking the value
 * into the pre-hydration markup (otherwise React will warn about a
 * hydration mismatch).
 */

import { readStorage, writeStorage } from '@/lib/safe-storage'

const STORAGE_KEY = 'tls.uid.v1'

let cached: string | null = null

export function getOrCreateTlsUid(): string {
  if (typeof window === 'undefined') return ''
  if (cached) return cached

  const existing = readStorage(STORAGE_KEY)
  if (existing && /^[0-9a-f-]{32,36}$/i.test(existing)) {
    cached = existing
    return existing
  }

  // On private mode / disabled storage the write no-ops and this becomes a
  // one-shot UUID for the session: it won't persist across reloads, but it
  // keeps analytics linkable within the page.
  const fresh = mintUuid()
  writeStorage(STORAGE_KEY, fresh)
  cached = fresh
  return fresh
}

function mintUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  // Fallback for very old browsers (no crypto.randomUUID): RFC4122-ish.
  const rnd = (n: number) => Math.floor(Math.random() * n)
  const hex = (n: number, len: number) => n.toString(16).padStart(len, '0')
  return `${hex(rnd(0x100000000), 8)}-${hex(rnd(0x10000), 4)}-4${hex(rnd(0x1000), 3)}-${hex(0x8 | rnd(0x4), 1)}${hex(rnd(0x1000), 3)}-${hex(rnd(0x1000000000000), 12)}`
}

/** Test-only: reset the module-level cache. Not exported in production usage. */
export function __resetTlsUidCacheForTests(): void {
  cached = null
}
